import { Box } from '@mui/material';
import { usePrevious } from 'common';
import { LruObjectCache } from 're-reselect';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  Index,
  List,
  ListRowProps,
  ScrollParams,
  WindowScroller,
} from 'react-virtualized';
import { OverscanIndicesGetterParams } from 'react-virtualized/dist/es/Grid';
import { debounce } from 'throttle-debounce';
import { BreakpointProperties } from '../../../../../hooks/useBreakpoint';
import {
  CfDataGridForStoreDataProps,
  CfDataGridProps,
  CfDataGridRowType,
  CfDataGridTableViewDefinition,
  CfRecomputeRowHeightsRefCallback,
  CfRowData,
  NavigableElement,
} from '../../../CfDataGridProps';
import {
  detectScrollBottom,
  getIsLoading,
  getItemIdsFromDataGridProps,
  getLastPage,
  getPagingMode,
  getTotalPageCount,
} from '../../../utility/data-grid-props';
import { getEstimatedRowHeight } from '../../../utility/estimate-row-heights';
import { overrideListScrollTo } from '../../../utility/scroll-to';
import { StyledGridBody } from './body.styles';
import {
  detectTableRowsChanged,
  getVirtualizedTableRows,
  recomputeTableRowHeights,
  recomputeTableRowHeightsByIndex,
  recomputeTableRowHeightsFromIndex,
} from './common';

/* 
Caches
*/
export const scrollToCache = new LruObjectCache({ cacheSize: 3 });
export function getScrollTo(id: string): { scrollTop: number; itemIndex: number } | undefined {
  return scrollToCache.get(id);
}
export function setScrollTo(id: string, itemIndex?: number | undefined): void {
  if (itemIndex === undefined) {
    scrollToCache.remove(id);
  } else {
    const scrollTop = document.documentElement.scrollTop;
    scrollToCache.set(id, { scrollTop, itemIndex });
  }
}

const cellMeasurerCaches = new LruObjectCache({ cacheSize: 5 });
function getCache(
  id: string,
  getEstimatedAverageRowHeight: ((type: CfDataGridRowType) => number) | undefined
): CellMeasurerCache {
  let cache = cellMeasurerCaches.get(id);
  if (!cache) {
    cache = new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: getEstimatedAverageRowHeight?.('row') ?? 118,
    });
    cellMeasurerCaches.set(id, cache);
  }
  return cache;
}
export const getVirtualizedTableViewCellMeasurerCache = getCache;

/* 
Virtualized Card Body
*/
export function CfDataGridTableViewVirtualizedBody<TItem, TAppState>(props: {
  dataGridProps: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>;
  tableViewDefinition: CfDataGridTableViewDefinition<TItem>;
  setNavigableElement: (navigableElement: NavigableElement, rowIndex: number, columnIndex: number) => void;
  breakpointProperties: BreakpointProperties;
  onNextPageRequested: (pageIndex: number) => void;
  focusClosestEnabledElement: (rowIndex: number, elementIndex?: number) => void;
  recomputeRowHeightsRef: CfRecomputeRowHeightsRefCallback;
}): JSX.Element {
  /* 
  List 
  */
  const listRef = useRef<List | null>(null);

  const scrollTo = getScrollTo(props.dataGridProps.id);
  const cache = getCache(
    props.dataGridProps.id,
    props.tableViewDefinition?.virtualizedProps?.getEstimatedAverageRowHeight
  );

  const gridId = props.dataGridProps.id;
  const virtualizedProps = props.tableViewDefinition?.virtualizedProps;
  const contained = virtualizedProps?.contained ?? false;

  const isLoading = getIsLoading(props.dataGridProps);
  const lastPage = getLastPage(props.dataGridProps);
  const pagingMode = getPagingMode(props.dataGridProps);
  const totalPageCount = getTotalPageCount(props.dataGridProps);
  const dataItemIds = getItemIdsFromDataGridProps(props.dataGridProps);

  /* 
  Functions 
  */
  const setListRef = (list: List | null) => {
    listRef.current = list;
    if (list) overrideListScrollTo(list);

    virtualizedProps?.recomputeRowHeightsRef?.(list ? () => recomputeTableRowHeights(listRef, cache) : null);
    virtualizedProps?.scrollIndexRef?.(list ? (i: number) => setScrollTo(gridId, i) : null);
    props.recomputeRowHeightsRef(() => {
      cache.clearAll();
    });
  };

  const recomputeRowHeights = () => recomputeTableRowHeights(listRef, cache);
  const recomputeRowHeightsFromIndex = (i: number) =>
    recomputeTableRowHeightsFromIndex(props.tableViewDefinition, listRef, cache, dataItemIds.length, i);
  const recomputeRowHeightsByIndex = (i: number) =>
    recomputeTableRowHeightsByIndex(props.tableViewDefinition, listRef, cache, i);

  const _debouncedRecomputeRowHeights = debounce(100, true, recomputeRowHeights);
  const _debouncedOnScrollBottom = debounce(150, true, detectScrollBottom);

  const debouncedRecomputeRowHeights = useCallback(_debouncedRecomputeRowHeights, [listRef]);
  const debouncedOnScrollBottom = useCallback(_debouncedOnScrollBottom, [listRef]);

  /* 
  Hooks
  */
  const { itemIds, rows } = useMemo(
    () =>
      getVirtualizedTableRows(
        props.dataGridProps,
        props.tableViewDefinition,
        props.setNavigableElement,
        recomputeRowHeightsByIndex,
        debouncedRecomputeRowHeights
      ),
    [props.dataGridProps.id, isLoading, props.breakpointProperties.deviceType, dataItemIds]
  );

  const prevIsLoading = usePrevious(isLoading);
  const prevDeviceType = usePrevious(props.breakpointProperties.deviceType);
  const prevItemIds = usePrevious(itemIds);

  /* 
  Effects
  */
  useLayoutEffect(() => {
    if (!listRef.current || !lastPage || scrollTo === undefined) return;

    // Handle scroll-to
    if (prevIsLoading === undefined && isLoading === false) {
      listRef.current?.scrollToPosition?.(scrollTo.scrollTop);
      props.focusClosestEnabledElement(scrollTo.itemIndex);
      scrollToCache.remove(gridId);
    } else {
      scrollToCache.remove(gridId);
    }
  }, [isLoading, scrollTo]);

  useEffect(() => {
    if (virtualizedProps?.getRowHeight || !listRef.current || !lastPage) return;

    // Handle device type
    if (prevDeviceType !== undefined && props.breakpointProperties.deviceType !== prevDeviceType) {
      //console.log('device type change');
      recomputeRowHeights();
      setTimeout(recomputeRowHeights, 20);
    }
  }, [props.breakpointProperties.deviceType]);

  useEffect(() => {
    if (virtualizedProps?.getRowHeight || !listRef.current || !lastPage) return;

    // Handle load
    if (lastPage.pageIndex === 0 && prevIsLoading != isLoading) {
      if (prevIsLoading === undefined && isLoading === false) return; // no-op, returning to page with grid data
      recomputeRowHeights();
    } else if (!isLoading) {
      const { index, differenceFound } = detectTableRowsChanged(itemIds, prevItemIds);
      //console.log(index, differenceFound, itemIds.length);

      if (differenceFound) recomputeRowHeightsFromIndex(index);
    }
  }, [isLoading, itemIds]);

  useEffect(() => {
    return () => {
      _debouncedRecomputeRowHeights.cancel();
      _debouncedOnScrollBottom.cancel();
    };
  }, []);

  /* 
  JSX 
  */
  const listPropsBase = {
    ref: setListRef,
    deferredMeasurementCache: cache,
    rowCount: rows.length,
    rowHeight: ({ index }: Index) => virtualizedProps?.getRowHeight?.(rows[index].type) ?? cache.rowHeight({ index }),
    rowRenderer: getRowRender(rows, cache),
    overscanRowCount: virtualizedProps?.overscanRowCount ?? 25,
    estimatedRowSize: getEstimatedRowHeight(
      rows,
      cache,
      virtualizedProps?.getEstimatedAverageRowHeight,
      prevIsLoading ?? false
    ),
    overscanIndicesGetter: (params: OverscanIndicesGetterParams) => {
      return {
        overscanStartIndex: Math.max(0, params.startIndex - params.overscanCellsCount),
        overscanStopIndex: Math.min(params.cellCount - 1, params.stopIndex + params.overscanCellsCount),
      };
    },
    tabIndex: -1,
  };

  // Handle max height
  const overflowRows = virtualizedProps?.rowsUntilContainedOverflow;
  let maxHeight = virtualizedProps?.maxHeight;
  const getDefaultRowHeight = (index: number) => {
    const row = rows[index];
    return virtualizedProps?.getDefaultRowHeight?.(row.type) ?? 0;
  };

  let maxHeightComputed = 0;
  if (contained && overflowRows) {
    let overflowHeight = 0;
    for (let i = 0; i < overflowRows && i < rows.length; i++) {
      const rowHeight = getDefaultRowHeight(i);
      overflowHeight += rowHeight;
    }
    maxHeightComputed = overflowHeight;
  }

  if (overflowRows && rows.length > overflowRows) {
    maxHeight = maxHeightComputed;
  }

  // Contain grid if the rows overflow, margins for scrollbar
  if (contained && maxHeight !== undefined) {
    return (
      <StyledGridBody
        sx={{
          flex: 1,
          height: maxHeight,
          mr: {
            xs: -1.75,
            xl: 0.25,
          },
          ...props.tableViewDefinition.tableBodySx,
        }}
      >
        <AutoSizer>
          {({ width, height }) => (
            <List
              {...listPropsBase}
              width={width}
              height={height}
              onScroll={(params: ScrollParams) =>
                debouncedOnScrollBottom(params, lastPage, pagingMode, totalPageCount, props.onNextPageRequested)
              }
            />
          )}
        </AutoSizer>
      </StyledGridBody>
    );
  }
  return (
    <StyledGridBody
      sx={{
        ...(contained
          ? { mr: { xl: 2 }, ...props.tableViewDefinition.tableBodySx }
          : props.tableViewDefinition.tableBodySx),
      }}
    >
      <WindowScroller>
        {({ height, isScrolling, onChildScroll, scrollTop, registerChild }) => (
          <AutoSizer disableHeight>
            {({ width }) => (
              <div ref={(el) => registerChild(el)}>
                <List
                  {...listPropsBase}
                  width={width}
                  height={height}
                  style={{ outline: 'none' }}
                  autoHeight
                  isScrolling={isScrolling}
                  onScroll={(params: ScrollParams) => {
                    debouncedOnScrollBottom(params, lastPage, pagingMode, totalPageCount, props.onNextPageRequested);
                    onChildScroll(params);
                  }}
                  scrollTop={scrollTop}
                />
              </div>
            )}
          </AutoSizer>
        )}
      </WindowScroller>
    </StyledGridBody>
  );
}

const getRowRender = (rows: CfRowData[], cache: CellMeasurerCache) =>
  function rowRender(props: ListRowProps): JSX.Element | null {
    return (
      <CellMeasurer cache={cache} key={props.key} parent={props.parent} rowIndex={props.index}>
        {({ registerChild }) => (
          <Box ref={registerChild} style={props.style} className={rows[props.index].type}>
            {rows[props.index].content}
          </Box>
        )}
      </CellMeasurer>
    );
  };
